<template>
  <div ref="scrollArea" class="ComparisonTableDesktop-scrollArea">
    <table class="ComparisonTableDesktop-table">
      <thead>
        <tr ref="track">
          <td class="ComparisonTableDesktop-corner" />

          <th
            v-for="column of columns"
            :key="column.planId"
            scope="col"
            class="ComparisonTableDesktop-header"
            :class="column.presentation.stateClass"
          >
            <ColumnHeader :column :tagProps="column.presentation.tag" />
          </th>
        </tr>
      </thead>

      <tbody>
        <tr v-for="(row, index) of rows" :key="index">
          <th scope="row" class="ComparisonTableDesktop-labelCell">
            {{ row.label }}
          </th>

          <td
            v-for="column of columns"
            :key="column.planId"
            class="ComparisonTableDesktop-cell"
            :class="column.presentation.stateClass"
          >
            <PriceCell
              v-if="row.isPrice"
              :price="column.price"
              :accent="column.state === 'promotion'"
            />
            <template v-else>
              {{ column.cells[index] ?? EMPTY_CELL }}
            </template>
          </td>
        </tr>
      </tbody>

      <tfoot>
        <tr>
          <td class="ComparisonTableDesktop-corner ComparisonTableDesktop-corner--cta" />

          <td
            v-for="column of columns"
            :key="column.planId"
            class="ComparisonTableDesktop-ctaCell"
            :class="column.presentation.stateClass"
          >
            <ActionButton
              v-if="column.submitLabel"
              :variant="column.presentation.ctaVariant"
              :disabled="column.submitDisabled"
              size="md"
              class="ComparisonTableDesktop-cta"
              @click="emit('submit', column.planId)"
            >
              {{ column.submitLabel }}
            </ActionButton>
          </td>
        </tr>
      </tfoot>
    </table>
  </div>

  <PageIndicator
    v-if="pageCount > 1"
    :modelValue="activePage"
    :count="pageCount"
    :dotLabel="pageLabel"
    class="ComparisonTableDesktop-pages"
    @update:model-value="scrollToPage"
  />
</template>

<script setup lang="ts">
import { watch, nextTick, useTemplateRef } from 'vue';
import ActionButton from '@cvo/ui/components/atoms/ActionButton/ActionButton.vue';
import PageIndicator from '@cvo/ui/components/atoms/PageIndicator/PageIndicator.vue';
import { usePagedScroll } from '@cvo/ui/composables';
import ColumnHeader from './ColumnHeader.vue';
import PriceCell from './PriceCell.vue';
import { EMPTY_CELL } from './BasePlanComparisonTable.model';
import type { ComparisonLayoutProps } from './BasePlanComparisonTable.model';

const props = defineProps<ComparisonLayoutProps>();

const emit = defineEmits<{ (e: 'submit', planId: string): void }>();

const scrollArea = useTemplateRef<HTMLElement>('scrollArea');
const track = useTemplateRef<HTMLElement>('track');

// The header row leads with the sticky label cell, which is not a page.
const { activePage, pageCount, measurePages, scrollToPage } = usePagedScroll({
  leading: 1,
  scrollArea,
  track,
});

// New columns or rows repage the table without resizing it, so the resize observer never fires.
watch(() => [props.columns, props.rows], () => nextTick(measurePages));
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/text';

// Figma column widths; the label column doubles as the scroll area's sticky offset.
$label-column-width: 160px;
$column-width: 347px;

// Figma row metrics, floors rather than fixed heights so long content grows the row.
$row-height: 44px;
$header-height: 76px;
$cta-height: 71px;

@layer cvo-ui {
  .ComparisonTableDesktop {
    // Rounded here so it clips the columns scrolling underneath.
    &-scrollArea {
      max-width: 100%;
      overflow-x: auto;
      background-color: vars.$organisms-table-comparison-table-bg-color;
      border: 1px solid vars.$organisms-table-comparison-table-border-color;
      border-radius: vars.$dimensions-d-5;
      scroll-snap-type: x proximity;
      // Keeps a revealed column clear of the sticky label column.
      scroll-padding-inline-start: $label-column-width;
    }

    &-table {
      // Separate, so the borders stop travelling with the sticky column.
      border-collapse: separate;
      border-spacing: 0;
      // Fixed, so the widths below are the used ones and their sum is the table's.
      table-layout: fixed;
      width: min-content;
    }

    // Opaque, since the plan columns scroll behind it.
    &-corner,
    &-labelCell {
      position: sticky;
      inset-inline-start: 0;
      z-index: 1;
      width: $label-column-width;
      background-color: vars.$organisms-table-comparison-table-bg-color;
      border-inline-end: 1px solid vars.$organisms-table-comparison-table-border-color;
      border-block-end: 1px solid vars.$organisms-table-comparison-table-border-color;
    }

    &-labelCell {
      height: $row-height;
      padding-inline: vars.$dimensions-d-5;
      text-align: start;
      vertical-align: middle;
      // Fixed layout pins the column, so a long token would run over the next one.
      overflow-wrap: break-word;

      @include text.text('sm', vars.$typography-weight-strong);
      color: vars.$organisms-table-comparison-table-label-color;
    }

    // The CTA row closes the table, so the last separator is the one above it.
    &-corner--cta {
      border-block-end: none;
    }

    &-header,
    &-cell,
    &-ctaCell {
      width: $column-width;
      background-color: vars.$organisms-table-comparison-table-content-bg-color;
      border-inline-end: 1px solid vars.$organisms-table-comparison-table-content-border-color;

      &:last-child {
        border-inline-end: none;
      }
    }

    &-cell.is-comingSoon {
      color: vars.$organisms-table-comparison-table-content-comingsoon-text-color;
    }

    &-header {
      height: $header-height;
      padding: vars.$dimensions-d-4 vars.$dimensions-d-5 0;
      border-block-end: 1px solid vars.$organisms-table-comparison-table-content-border-color;
      text-align: start;
      vertical-align: top;
      scroll-snap-align: start;
    }

    &-cell {
      height: $row-height;
      padding-inline: vars.$dimensions-d-5;
      border-block-end: 1px solid vars.$organisms-table-comparison-table-content-border-color;
      vertical-align: middle;
      overflow-wrap: break-word;

      @include text.text('sm');
      color: vars.$organisms-table-comparison-table-content-text-color;
    }

    &-ctaCell {
      height: $cta-height;
      padding-inline: vars.$dimensions-d-5;
      vertical-align: middle;
    }

    &-cta {
      width: 100%;
    }

    &-pages {
      flex-shrink: 0;
    }
  }
}
</style>
